import {useEffect, useState} from "react";

import CrudSelect from "components/crud/CrudSelect";

import api from "services/api/axios";


const EMPTY_OPTIONS = [];


const LocationCascade = ({form, setForm}) => {

  // =========================================================
  // OPTIONS
  // =========================================================

  const [countries, setCountries] = useState(EMPTY_OPTIONS);

  const [states, setStates] = useState(EMPTY_OPTIONS);

  const [districts, setDistricts] = useState(EMPTY_OPTIONS);

  const [cities, setCities] = useState(EMPTY_OPTIONS);

  const [zones, setZones] = useState(EMPTY_OPTIONS);

  const [areas, setAreas] = useState(EMPTY_OPTIONS);

  const [beats, setBeats] = useState(EMPTY_OPTIONS);


  const [territories, setTerritories] = useState(EMPTY_OPTIONS);


  // =========================================================
  // LOADING
  // =========================================================

  const [loadingCountries, setLoadingCountries] = useState(false);

  const [loadingStates, setLoadingStates] = useState(false);

  const [loadingDistricts, setLoadingDistricts] = useState(false);

  const [loadingCities, setLoadingCities] = useState(false);

  const [loadingZones, setLoadingZones] = useState(false);

  const [loadingAreas, setLoadingAreas] = useState(false);

  const [loadingBeats, setLoadingBeats] = useState(false);

  const [loadingTerritories, setLoadingTerritories] = useState(false);

  // =========================================================
  // NORMALIZE API RESPONSE
  // =========================================================

  const extractItems = (
    response
  ) => {

    return (
      response?.data?.items ||
      response?.data?.records ||
      response?.data?.countries ||
      response?.data?.states ||
      response?.data?.districts ||
      response?.data?.cities ||
      response?.data?.zones ||
      response?.data?.areas ||
      response?.data?.beats ||
      response?.data?.territories ||
      []
    );

  };


  // =========================================================
  // NORMALIZE OPTION
  // =========================================================

  const normalizeOptions = (
    items
  ) => {

    if (
      !Array.isArray(items)
    ) {
      return [];
    }


    return items.map(
      (item) => ({
        value:
          String(
            item.id
          ),

        label:
          item.name ||
          item.code ||
          "-",

        code:
          item.code ||
          "",
      })
    );

  };


  // =========================================================
  // LOAD COUNTRIES
  //
  // Country is independent for the School form.
  // Therefore we load all active countries instead of requiring
  // the user to first select a Business Region.
  // =========================================================

  const loadCountries =
    async () => {

      try {

        setLoadingCountries(
          true
        );


        const response =
          await api.get(
            "/organization/countries",
            {
              params: {
                page: 1,
                limit: 1000,
                status: "active",
              },
            }
          );


        const items =
          extractItems(
            response.data
          );


        setCountries(
          normalizeOptions(
            items
          )
        );

      } catch (
        error
      ) {

        console.error(
          "Country loading error:",
          error
        );

        setCountries([]);

      } finally {

        setLoadingCountries(
          false
        );

      }

    };


  // =========================================================
  // LOAD STATES
  // =========================================================

  const loadStates =
    async (
      countryId
    ) => {

      if (!countryId) {

        setStates([]);

        return;

      }


      try {

        setLoadingStates(
          true
        );


        const response =
          await api.get(
            `/organization/countries/${countryId}/states`
          );


        const items =
          extractItems(
            response.data
          );


        setStates(
          normalizeOptions(
            items
          )
        );

      } catch (
        error
      ) {

        console.error(
          "State loading error:",
          error
        );

        setStates([]);

      } finally {

        setLoadingStates(
          false
        );

      }

    };


  // =========================================================
  // LOAD DISTRICTS
  // =========================================================

  const loadDistricts =
    async (
      stateId
    ) => {

      if (!stateId) {

        setDistricts([]);

        return;

      }


      try {

        setLoadingDistricts(
          true
        );


        const response =
          await api.get(
            `/organization/states/${stateId}/districts`
          );


        const items =
          extractItems(
            response.data
          );


        setDistricts(
          normalizeOptions(
            items
          )
        );

      } catch (
        error
      ) {

        console.error(
          "District loading error:",
          error
        );

        setDistricts([]);

      } finally {

        setLoadingDistricts(
          false
        );

      }

    };


  // =========================================================
  // LOAD CITIES
  // =========================================================

  const loadCities =
    async (
      districtId
    ) => {

      if (!districtId) {

        setCities([]);

        return;

      }


      try {

        setLoadingCities(
          true
        );


        const response =
          await api.get(
            `/organization/districts/${districtId}/cities`
          );


        const items =
          extractItems(
            response.data
          );


        setCities(
          normalizeOptions(
            items
          )
        );

      } catch (
        error
      ) {

        console.error(
          "City loading error:",
          error
        );

        setCities([]);

      } finally {

        setLoadingCities(
          false
        );

      }

    };


  // =========================================================
  // LOAD ZONES
  // =========================================================

  const loadZones =
    async (
      cityId
    ) => {

      if (!cityId) {

        setZones([]);

        return;

      }


      try {

        setLoadingZones(
          true
        );


        const response =
          await api.get(
            `/organization/cities/${cityId}/zones`
          );


        const items =
          extractItems(
            response.data
          );


        setZones(
          normalizeOptions(
            items
          )
        );

      } catch (
        error
      ) {

        console.error(
          "Zone loading error:",
          error
        );

        setZones([]);

      } finally {

        setLoadingZones(
          false
        );

      }

    };


  // =========================================================
  // LOAD AREAS
  // =========================================================

  const loadAreas =
    async (
      zoneId
    ) => {

      if (!zoneId) {

        setAreas([]);

        return;

      }


      try {

        setLoadingAreas(
          true
        );


        const response =
          await api.get(
            `/organization/zones/${zoneId}/areas`
          );


        const items =
          extractItems(
            response.data
          );


        setAreas(
          normalizeOptions(
            items
          )
        );

      } catch (
        error
      ) {

        console.error(
          "Area loading error:",
          error
        );

        setAreas([]);

      } finally {

        setLoadingAreas(
          false
        );

      }

    };


  // =========================================================
  // LOAD BEATS
  // =========================================================

  const loadBeats =
    async (
      areaId
    ) => {

      if (!areaId) {

        setBeats([]);

        return;

      }


      try {

        setLoadingBeats(
          true
        );


        const response =
          await api.get(
            `/organization/areas/${areaId}/beats`
          );


        const items =
          extractItems(
            response.data
          );


        setBeats(
          normalizeOptions(
            items
          )
        );

      } catch (
        error
      ) {

        console.error(
          "Beat loading error:",
          error
        );

        setBeats([]);

      } finally {

        setLoadingBeats(
          false
        );

      }

    };


  // =========================================================
  // LOAD TERRITORIES
  // =========================================================

  const loadTerritories =
    async (
      beatId
    ) => {

      if (!beatId) {

        setTerritories([]);

        return;

      }


      try {

        setLoadingTerritories(
          true
        );


        const response =
          await api.get(
            `/organization/beats/${beatId}/territories`
          );


        const items =
          extractItems(
            response.data
          );


        setTerritories(
          normalizeOptions(
            items
          )
        );

      } catch (
        error
      ) {

        console.error(
          "Territory loading error:",
          error
        );

        setTerritories([]);

      } finally {

        setLoadingTerritories(
          false
        );

      }

    };


  // =========================================================
  // INITIAL COUNTRIES
  // =========================================================

  useEffect(() => {

    loadCountries();

  }, []);


  // =========================================================
  // COUNTRY → STATE
  //
  // Important:
  // This only loads states.
  // It does NOT clear existing values.
  //
  // That is necessary when editing an existing School.
  // =========================================================

  useEffect(() => {

    if (
      form.countryId
    ) {

      loadStates(
        form.countryId
      );

    } else {

      setStates([]);

    }

  }, [
    form.countryId,
  ]);


  // =========================================================
  // STATE → DISTRICT
  // =========================================================

  useEffect(() => {

    if (
      form.stateId
    ) {

      loadDistricts(
        form.stateId
      );

    } else {

      setDistricts([]);

    }

  }, [
    form.stateId,
  ]);


  // =========================================================
  // DISTRICT → CITY
  // =========================================================

  useEffect(() => {

    if (
      form.districtId
    ) {

      loadCities(
        form.districtId
      );

    } else {

      setCities([]);

    }

  }, [
    form.districtId,
  ]);


  // =========================================================
  // CITY → ZONE
  // =========================================================

  useEffect(() => {

    if (
      form.cityId
    ) {

      loadZones(
        form.cityId
      );

    } else {

      setZones([]);

    }

  }, [
    form.cityId,
  ]);


  // =========================================================
  // ZONE → AREA
  // =========================================================

  useEffect(() => {

    if (
      form.zoneId
    ) {

      loadAreas(
        form.zoneId
      );

    } else {

      setAreas([]);

    }

  }, [
    form.zoneId,
  ]);


  // =========================================================
  // AREA → BEAT
  // =========================================================

  useEffect(() => {

    if (
      form.areaId
    ) {

      loadBeats(
        form.areaId
      );

    } else {

      setBeats([]);

    }

  }, [
    form.areaId,
  ]);


  // =========================================================
  // BEAT → TERRITORY
  // =========================================================

  useEffect(() => {

    if (
      form.beatId
    ) {

      loadTerritories(
        form.beatId
      );

    } else {

      setTerritories([]);

    }

  }, [
    form.beatId,
  ]);


  // =========================================================
  // COUNTRY CHANGE
  // =========================================================

  const handleCountryChange =
    (event) => {

      const value =
        event.target.value;


      setForm(
        (previous) => ({
          ...previous,

          countryId:
            value,

          stateId: "",
          districtId: "",
          cityId: "",
          zoneId: "",
          areaId: "",
          beatId: "",
          territoryId: "",
        })
      );


      setStates([]);

      setDistricts([]);

      setCities([]);

      setZones([]);

      setAreas([]);

      setBeats([]);

      setTerritories([]);

    };


  // =========================================================
  // STATE CHANGE
  // =========================================================

  const handleStateChange =
    (event) => {

      const value =
        event.target.value;


      setForm(
        (previous) => ({
          ...previous,

          stateId:
            value,

          districtId: "",
          cityId: "",
          zoneId: "",
          areaId: "",
          beatId: "",
          territoryId: "",
        })
      );


      setDistricts([]);

      setCities([]);

      setZones([]);

      setAreas([]);

      setBeats([]);

      setTerritories([]);

    };


  // =========================================================
  // DISTRICT CHANGE
  // =========================================================

  const handleDistrictChange =
    (event) => {

      const value =
        event.target.value;


      setForm(
        (previous) => ({
          ...previous,

          districtId:
            value,

          cityId: "",
          zoneId: "",
          areaId: "",
          beatId: "",
          territoryId: "",
        })
      );


      setCities([]);

      setZones([]);

      setAreas([]);

      setBeats([]);

      setTerritories([]);

    };


  // =========================================================
  // CITY CHANGE
  // =========================================================

  const handleCityChange =
    (event) => {

      const value =
        event.target.value;


      setForm(
        (previous) => ({
          ...previous,

          cityId:
            value,

          zoneId: "",
          areaId: "",
          beatId: "",
          territoryId: "",
        })
      );


      setZones([]);

      setAreas([]);

      setBeats([]);

      setTerritories([]);

    };


  // =========================================================
  // ZONE CHANGE
  // =========================================================

  const handleZoneChange =
    (event) => {

      const value =
        event.target.value;


      setForm(
        (previous) => ({
          ...previous,

          zoneId:
            value,

          areaId: "",
          beatId: "",
          territoryId: "",
        })
      );


      setAreas([]);

      setBeats([]);

      setTerritories([]);

    };


  // =========================================================
  // AREA CHANGE
  // =========================================================

  const handleAreaChange =
    (event) => {

      const value =
        event.target.value;


      setForm(
        (previous) => ({
          ...previous,

          areaId:
            value,

          beatId: "",
          territoryId: "",
        })
      );


      setBeats([]);

      setTerritories([]);

    };


  // =========================================================
  // BEAT CHANGE
  // =========================================================

  const handleBeatChange =
    (event) => {

      const value =
        event.target.value;


      setForm(
        (previous) => ({
          ...previous,

          beatId:
            value,

          territoryId: "",
        })
      );


      setTerritories([]);

    };


  // =========================================================
  // RENDER
  // =========================================================

  return (

    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

      {/* =====================================================
          COUNTRY
      ===================================================== */}

      <CrudSelect
        label="Country"
        required

        value={
          String(
            form.countryId ||
            ""
          )
        }

        onChange={
          handleCountryChange
        }

        options={[
          {
            value: "",
            label:
              loadingCountries
                ? "Loading countries..."
                : "Select Country",
          },

          ...countries.map(
            (country) => ({
              value:
                String(
                  country.value
                ),

              label:
                country.label,
            })
          ),
        ]}
      />


      {/* =====================================================
          STATE
      ===================================================== */}

      <CrudSelect
        label="State / Province"
        required

        value={
          String(
            form.stateId ||
            ""
          )
        }

        onChange={
          handleStateChange
        }

        options={[
          {
            value: "",
            label:
              !form.countryId
                ? "Select Country First"
                : loadingStates
                  ? "Loading states..."
                  : "Select State",
          },

          ...states.map(
            (state) => ({
              value:
                String(
                  state.value
                ),

              label:
                state.label,
            })
          ),
        ]}
      />


      {/* =====================================================
          DISTRICT
      ===================================================== */}

      <CrudSelect
        label="District"
        required

        value={
          String(
            form.districtId ||
            ""
          )
        }

        onChange={
          handleDistrictChange
        }

        options={[
          {
            value: "",
            label:
              !form.stateId
                ? "Select State First"
                : loadingDistricts
                  ? "Loading districts..."
                  : "Select District",
          },

          ...districts.map(
            (district) => ({
              value:
                String(
                  district.value
                ),

              label:
                district.label,
            })
          ),
        ]}
      />


      {/* =====================================================
          CITY
      ===================================================== */}

      <CrudSelect
        label="City / Town"
        required

        value={
          String(
            form.cityId ||
            ""
          )
        }

        onChange={
          handleCityChange
        }

        options={[
          {
            value: "",
            label:
              !form.districtId
                ? "Select District First"
                : loadingCities
                  ? "Loading cities..."
                  : "Select City",
          },

          ...cities.map(
            (city) => ({
              value:
                String(
                  city.value
                ),

              label:
                city.label,
            })
          ),
        ]}
      />


      {/* =====================================================
          ZONE
      ===================================================== */}

      <CrudSelect
        label="City Zone"

        value={
          String(
            form.zoneId ||
            ""
          )
        }

        onChange={
          handleZoneChange
        }

        options={[
          {
            value: "",
            label:
              !form.cityId
                ? "Select City First"
                : loadingZones
                  ? "Loading zones..."
                  : "Select Zone",
          },

          ...zones.map(
            (zone) => ({
              value:
                String(
                  zone.value
                ),

              label:
                zone.label,
            })
          ),
        ]}
      />


      {/* =====================================================
          AREA
      ===================================================== */}

      <CrudSelect
        label="Area / Locality"

        value={
          String(
            form.areaId ||
            ""
          )
        }

        onChange={
          handleAreaChange
        }

        options={[
          {
            value: "",
            label:
              !form.zoneId
                ? "Select Zone First"
                : loadingAreas
                  ? "Loading areas..."
                  : "Select Area",
          },

          ...areas.map(
            (area) => ({
              value:
                String(
                  area.value
                ),

              label:
                area.label,
            })
          ),
        ]}
      />


      {/* =====================================================
          BEAT
      ===================================================== */}

      <CrudSelect
        label="Beat"

        value={
          String(
            form.beatId ||
            ""
          )
        }

        onChange={
          handleBeatChange
        }

        options={[
          {
            value: "",
            label:
              !form.areaId
                ? "Select Area First"
                : loadingBeats
                  ? "Loading beats..."
                  : "Select Beat",
          },

          ...beats.map(
            (beat) => ({
              value:
                String(
                  beat.value
                ),

              label:
                beat.label,
            })
          ),
        ]}
      />


      {/* =====================================================
          TERRITORY
      ===================================================== */}

      <CrudSelect
        label="Territory"

        value={
          String(
            form.territoryId ||
            ""
          )
        }

        onChange={(event) => {

          setForm(
            (previous) => ({
              ...previous,

              territoryId:
                event.target.value,
            })
          );

        }}

        options={[
          {
            value: "",
            label:
              !form.beatId
                ? "Select Beat First"
                : loadingTerritories
                  ? "Loading territories..."
                  : "Select Territory",
          },

          ...territories.map(
            (territory) => ({
              value:
                String(
                  territory.value
                ),

              label:
                territory.label,
            })
          ),
        ]}
      />

    </div>
  );
};


export default LocationCascade;